import { Calendar, CreditCard, Sparkles, Edit3, Trash2, ArrowUp, AlertCircle } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// FIN-6 — AssinaturaCard.
// Card visual de uma assinatura com avatar merchant + status badge +
// alerta sutil de subida + 3 ações no hover.

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

function _isoToBR(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}`;
  } catch { return iso; }
}

const FREQ_LABEL = {
  monthly: "/ mês",
  annual: "/ ano",
  weekly: "/ semana",
  custom: "",
};

const STATUS_META = {
  active:   { label: "Ativa",     variant: "success" },
  paused:   { label: "Pausada",   variant: "warning" },
  canceled: { label: "Cancelada", variant: "outline" },
};

// Avatar palette determinístico — hash do merchant
const AVATAR_PALETTE = [
  ["#FF4D6D", "#FFFFFF"], ["#06B6D4", "#FFFFFF"], ["#22C55E", "#FFFFFF"],
  ["#A855F7", "#FFFFFF"], ["#F59E0B", "#FFFFFF"], ["#EC4899", "#FFFFFF"],
  ["#10B981", "#FFFFFF"], ["#3B82F6", "#FFFFFF"], ["#F97316", "#FFFFFF"],
  ["#8B5CF6", "#FFFFFF"],
];

function _avatarFor(merchant) {
  if (!merchant) return { bg: "#94A3B8", color: "#FFFFFF", initials: "?" };
  const cleaned = String(merchant).trim().replace(/\s+/g, " ");
  const initials = cleaned.split(" ").slice(0, 2).map(w => w[0] || "").join("").toUpperCase() || "?";
  let h = 0;
  for (const ch of cleaned) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const [bg, color] = AVATAR_PALETTE[h % AVATAR_PALETTE.length];
  return { bg, color, initials };
}

function VencimentoBadge({ days, status }) {
  if (status !== "active" || days == null) return null;
  if (days < 0) {
    return <Badge variant="danger" className="text-micro">Atrasada há {Math.abs(days)}d</Badge>;
  }
  if (days === 0) {
    return <Badge variant="danger" className="text-micro">Hoje</Badge>;
  }
  if (days <= 7) {
    return <Badge variant="warning" className="text-micro">Em {days}d</Badge>;
  }
  return null;
}

export default function AssinaturaCard({
  sub,
  onEdit,
  onDelete,
  onTalk,
}) {
  const status = sub.status || "active";
  const statusMeta = STATUS_META[status] || STATUS_META.active;
  const isCanceled = status === "canceled";
  const avatar = _avatarFor(sub.merchant || sub.name);
  const freqLabel = FREQ_LABEL[sub.frequency] || "";

  return (
    <Card
      variant="elevated"
      className={cn(
        "glass-card glow-hover p-5 group/sub relative flex flex-col gap-3",
        isCanceled && "opacity-60",
        // Diferencial Nexo: card com border amber quando preço subiu
        sub.price_increased && !isCanceled && "border-status-warning/40 shadow-[0_0_18px_rgba(245,158,11,0.18)]",
      )}
      data-testid={`sub-card-${sub.id}`}
    >
      {/* Header: avatar + nome + status */}
      <div className="flex items-start gap-3">
        <div
          className="w-11 h-11 rounded-lg flex items-center justify-center font-display font-bold text-base shrink-0"
          style={{ backgroundColor: avatar.bg, color: avatar.color }}
          aria-hidden="true"
        >
          {avatar.initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-foreground font-semibold truncate">{sub.name}</p>
          <p className="text-micro text-foreground-muted truncate">{sub.merchant}</p>
        </div>
        <Badge variant={statusMeta.variant} className="text-micro shrink-0">{statusMeta.label}</Badge>
      </div>

      {/* Valor + frequência */}
      <div className="flex items-baseline gap-1.5">
        <span className="font-mono font-bold text-2xl text-foreground tabular-nums">
          {fmtBRL(sub.amount)}
        </span>
        <span className="text-caption text-foreground-muted">{freqLabel}</span>
      </div>

      {/* Diferencial Nexo: alerta sutil de subida */}
      {sub.price_increased && sub.previous_amount != null && (
        <Badge variant="warning" className="self-start text-micro inline-flex items-center gap-1" data-testid={`sub-card-${sub.id}-price-up`}>
          <ArrowUp className="w-3 h-3" />
          Subiu {fmtBRL(sub.amount - sub.previous_amount)} (+{Math.abs(sub.price_change_pct).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%)
        </Badge>
      )}

      {/* Próxima cobrança */}
      {sub.next_billing_date && (
        <div className="flex items-center gap-2 text-caption">
          <Calendar className="w-3.5 h-3.5 text-foreground-muted shrink-0" aria-hidden="true" />
          <span className="text-foreground-muted">
            Próxima: <span className="text-foreground">{_isoToBR(sub.next_billing_date)}</span>
            {sub.days_to_next_billing != null && status === "active" && sub.days_to_next_billing >= 0 && (
              <span className="text-foreground-muted ml-1">(em {sub.days_to_next_billing}d)</span>
            )}
          </span>
          <VencimentoBadge days={sub.days_to_next_billing} status={status} />
        </div>
      )}

      {/* Pills inferiores: categoria + payment */}
      <div className="flex items-center gap-2 mt-auto flex-wrap">
        {sub.category && (
          <Badge variant="info" className="text-micro">{sub.category}</Badge>
        )}
        {sub.payment_method && (
          <span className="inline-flex items-center gap-1 text-micro text-foreground-muted">
            <CreditCard className="w-3 h-3" aria-hidden="true" />
            {sub.payment_method}
          </span>
        )}
      </div>

      {/* Hover actions */}
      <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover/sub:opacity-100 transition-opacity">
        {onTalk && (
          <button
            type="button"
            onClick={() => onTalk(sub)}
            className="p-1.5 rounded-md bg-brand-cyan/10 text-brand-cyan hover:bg-brand-cyan/25 transition-colors"
            aria-label="Falar com Nexo sobre esta assinatura"
            data-testid={`sub-card-${sub.id}-talk`}
          >
            <Sparkles className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={() => onEdit?.(sub)}
          className="p-1.5 rounded-md bg-surface-card-hover text-foreground-muted hover:text-foreground transition-colors"
          aria-label="Editar"
          data-testid={`sub-card-${sub.id}-edit`}
        >
          <Edit3 className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onDelete?.(sub)}
          className="p-1.5 rounded-md bg-status-error/10 text-status-error/80 hover:bg-status-error/20 hover:text-status-error transition-colors"
          aria-label="Apagar"
          data-testid={`sub-card-${sub.id}-delete`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </Card>
  );
}
